/**
 * Checkable menu rows for popover menus.
 *
 * Visually identical to `MenuItem` plus a leading
 * checkmark column when the row is active. `MenuRadioRow` is the mutually
 * exclusive variant used for selection groups; `MenuCheckRow` is the generic
 * toggle variant used for independent on/off items.
 *
 * Designed for use inside `<Popover>` — sets `data-popover-item` so the
 * popover's arrow-key navigation and `data-popover-active` highlight work
 * automatically.
 */

import { Check } from "lucide-react";
import { COLOR_TEXT_DIM, FONT_SIZE_BODY } from "../tokens.ts";
import { MenuButtonRow } from "./MenuPrimitives.tsx";

function MenuCheckableRow({
  label,
  checked,
  shortcut,
  disabled,
  popoverItem = true,
  onClick,
}: {
  label: string;
  checked: boolean;
  shortcut?: string;
  disabled?: boolean;
  popoverItem?: boolean;
  onClick: () => void;
}) {
  return (
    <MenuButtonRow
      data-popover-item={popoverItem ? true : undefined}
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
    >
      <span
        style={{
          width: 16,
          display: "inline-flex",
          justifyContent: "center",
          marginRight: 8,
        }}
      >
        {checked ? <Check size={13} /> : null}
      </span>
      <span style={{ flex: 1 }}>{label}</span>
      {shortcut && (
        <span
          style={{
            fontSize: FONT_SIZE_BODY - 1,
            color: COLOR_TEXT_DIM,
            marginLeft: 16,
          }}
        >
          {shortcut}
        </span>
      )}
    </MenuButtonRow>
  );
}

export function MenuCheckRow({
  label,
  checked,
  shortcut,
  disabled,
  popoverItem = true,
  onClick,
}: {
  label: string;
  checked: boolean;
  shortcut?: string;
  disabled?: boolean;
  popoverItem?: boolean;
  onClick: () => void;
}) {
  return (
    <MenuCheckableRow
      label={label}
      checked={checked}
      shortcut={shortcut}
      disabled={disabled}
      popoverItem={popoverItem}
      onClick={onClick}
    />
  );
}

export function MenuRadioRow({
  label,
  selected,
  shortcut,
  disabled,
  onClick,
}: {
  label: string;
  selected: boolean;
  shortcut?: string;
  disabled?: boolean;
  onClick: () => void;
}) {
  return (
    <MenuCheckableRow
      label={label}
      checked={selected}
      shortcut={shortcut}
      disabled={disabled}
      onClick={onClick}
    />
  );
}
